export type LessonCompletionStatus = "completed" | "unlocked" | "locked" | string;

export interface LessonNavInfo {
  id: string;
  order?: number;
  status: LessonCompletionStatus;
}

export interface LevelNavInfo {
  id: string;
  levelNumber: number;
  title?: string;
  status?: string;
}

export function levelsIncludedInPlan<T extends LevelNavInfo>(
  levels: T[] | undefined
): T[] {
  return (levels || []).filter((level) => level.status !== "locked_by_plan");
}

export function sortLessonsByOrder<T extends LessonNavInfo>(lessons: T[]): T[] {
  return [...lessons].sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function findNextIncompleteLessonInLevel(
  lessons: LessonNavInfo[] | undefined,
  currentLessonId: string | undefined
): LessonNavInfo | null {
  if (!lessons || !currentLessonId || lessons.length === 0) {
    return null;
  }

  const sortedLessons = sortLessonsByOrder(lessons);
  const currentIndex = sortedLessons.findIndex((lesson) => lesson.id === currentLessonId);
  if (currentIndex === -1) {
    return null;
  }

  return (
    sortedLessons
      .slice(currentIndex + 1).concat(sortedLessons.slice(0, currentIndex))
      .find((lesson) => lesson.status !== "completed" && lesson.status !== "locked") || null
  );
}

export function findNextLevel(
  allLevels: LevelNavInfo[] | undefined,
  currentLevelNumber: number | undefined
): LevelNavInfo | null {
  if (!allLevels || !currentLevelNumber || allLevels.length === 0) {
    return null;
  }

  const sortedLevels = [...allLevels].sort((a, b) => a.levelNumber - b.levelNumber);
  const currentIndex = sortedLevels.findIndex(
    (level) => level.levelNumber === currentLevelNumber
  );
  if (currentIndex === -1 || currentIndex >= sortedLevels.length - 1) {
    return null;
  }

  return sortedLevels[currentIndex + 1];
}

export function firstIncompleteUnlockedLesson(
  lessons: LessonNavInfo[] | undefined
): LessonNavInfo | null {
  if (!lessons || lessons.length === 0) {
    return null;
  }

  return (
    sortLessonsByOrder(lessons).find(
      (lesson) => lesson.status !== "completed" && lesson.status !== "locked"
    ) || null
  );
}

// Prefer forward progress, then any earlier unfinished module in this package.
export function findRemainingLevel(
  allLevels: LevelNavInfo[] | undefined, currentLevelNumber: number | undefined
): LevelNavInfo | null {
  const levels = [...(allLevels || [])].sort((a, b) => a.levelNumber - b.levelNumber);
  const remaining = levels.filter((level) => level.levelNumber !== currentLevelNumber &&
    !["completed", "locked", "locked_by_plan"].includes(level.status || ""));
  return remaining.find((level) => level.levelNumber > (currentLevelNumber || 0)) || remaining[0] || null;
}
